import { Check, ChevronDown, ChevronRight, Laptop, TriangleAlert, Usb } from "lucide-react";
import { cn } from "@/lib/utils";
import "./ready.css";

export const Frame = ({ children, testId, caption }) => (
  <figure className="hp-frame grid-bg flex flex-col items-center justify-center gap-4 rounded-2xl border bg-muted/40 p-5" data-testid={testId} aria-hidden="true">
    {children}
    {caption && <figcaption className="text-center text-xs font-medium text-muted-foreground">{caption}</figcaption>}
  </figure>
);

export const Window = ({ title, children, className }) => (
  <div className={cn("w-full max-w-[260px] overflow-hidden rounded-lg border bg-card text-[11px] shadow-lg", className)}>
    <div className="flex items-center justify-between border-b bg-muted/60 px-2.5 py-1.5 font-semibold">
      <span className="truncate">{title}</span><span className="tracking-[0.3em] text-muted-foreground">–□✕</span>
    </div>
    <div className="p-2">{children}</div>
  </div>
);

const Hot = ({ on, children, className }) => (
  <div className={cn("rounded px-2 py-1", on ? "bg-primary font-semibold text-primary-foreground" : "text-muted-foreground", className)}>{children}</div>
);

export const MiniHubFace = ({ children, pixels = [] }) => (
  <div className="relative rounded-2xl border-2 border-foreground/80 bg-background px-3 pb-3 pt-7 shadow-sm">
    {children}
    <div className="grid grid-cols-5 gap-[3px]">
      {Array.from({ length: 25 }).map((_, i) => <span key={i} className={cn("h-2.5 w-2.5 rounded-[2px]", pixels.includes(i) ? "bg-accent dark:bg-primary" : "bg-muted")} />)}
    </div>
    <span className="mx-auto mt-2 block h-3 w-3 rounded-full border-2 border-foreground/40" />
  </div>
);

export const HubUpdateMode = () => (
  <Frame testId="ready-visual-update-mode" caption="Hold the Bluetooth button, plug in, wait for red-green-blue">
    <div className="flex items-center">
      <Laptop className="h-12 w-12 shrink-0 text-muted-foreground" strokeWidth={1.5} />
      <span className="flex items-center"><span className="h-1 w-10 rounded bg-foreground/60" /><Usb className="-ml-1 h-4 w-4 text-foreground/70" /></span>
      <MiniHubFace>
        <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center">
          <span className="hp-press absolute inset-0 rounded-full border-2 border-primary" />
          <span className="hp-bt-light h-3 w-3 rounded-full" />
        </span>
      </MiniHubFace>
    </div>
  </Frame>
);

const WinLogo = () => (
  <span className="grid grid-cols-2 gap-[2px]">{[0, 1, 2, 3].map((i) => <span key={i} className="h-2 w-2 bg-primary" />)}</span>
);

const START_ITEMS = ["Apps and Features", "Power Options", "Event Viewer", "System", "Device Manager", "Network Connections", "Disk Management"];

export const StartMenuMock = () => (
  <Frame testId="ready-visual-start-menu" caption="Right-click Start, then Device Manager">
    <div className="w-48 rounded-lg border bg-card py-1 text-[11px] shadow-lg">
      {START_ITEMS.map((t) => <Hot key={t} on={t === "Device Manager"} className="mx-1">{t}</Hot>)}
    </div>
    <span className="flex items-center gap-2 self-start rounded-md border bg-card px-2 py-1.5 text-[10px] font-semibold"><WinLogo />Right-click</span>
  </Frame>
);

const TreeGroup = ({ label, open, children }) => (
  <div>
    <div className="flex items-center gap-1 px-1 py-0.5 text-muted-foreground">{open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}{label}</div>
    {open && <div className="pl-4">{children}</div>}
  </div>
);

const HubRow = ({ ok }) => (
  <Hot on className="flex items-center gap-1.5">
    {ok ? <Check className="h-3 w-3" /> : <TriangleAlert className="h-3 w-3 text-yellow-300" />}LEGO Technic Large Hub in DFU Mode
  </Hot>
);

export const DeviceTree = ({ group }) => (
  <Frame testId={`ready-visual-device-${group}`} caption={group === "usb" ? "Now under Universal Serial Bus devices, no warning sign" : "Under Other devices, with a yellow warning sign"}>
    <Window title="Device Manager">
      <TreeGroup label="Batteries" />
      <TreeGroup label="Bluetooth" />
      <TreeGroup label="Other devices" open={group === "other"}><HubRow /></TreeGroup>
      <TreeGroup label="Universal Serial Bus devices" open={group === "usb"}><HubRow ok /></TreeGroup>
      <TreeGroup label="Universal Serial Bus controllers" />
    </Window>
  </Frame>
);

const PATH = ["Update driver", "Browse my computer for drivers", "Let me pick from a list of available drivers on my computer"];

export const DriverPath = () => (
  <Frame testId="ready-visual-driver-path" caption="Three clicks, in this order">
    <div className="flex w-full max-w-[260px] flex-col items-stretch gap-1.5 text-[11px]">
      {PATH.map((t, i) => (
        <div key={t} className="flex flex-col items-center gap-1.5">
          <span className="hp-seq w-full rounded-lg border px-3 py-2 font-semibold" style={{ animationDelay: `${i * 1.1}s` }}>{i + 1}. {t}</span>
          {i < PATH.length - 1 && <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      ))}
    </div>
  </Frame>
);

export const WinUsbPicker = () => (
  <Frame testId="ready-visual-winusb" caption="Universal Serial Bus devices, then WinUsb Device on both sides">
    <Window title="Update Drivers">
      <p className="px-1 pb-1 font-semibold">Common hardware types</p>
      <Hot>Printers</Hot><Hot on>Universal Serial Bus devices</Hot>
      <div className="mt-2 grid grid-cols-2 gap-2 border-t pt-2">
        <div><p className="px-1 pb-1 font-semibold">Manufacturer</p><Hot on>WinUsb Device</Hot></div>
        <div><p className="px-1 pb-1 font-semibold">Model</p><Hot on>WinUsb Device</Hot></div>
      </div>
    </Window>
  </Frame>
);

export const NoDriverNeeded = () => (
  <Frame testId="ready-visual-no-driver" caption="Mac, Chromebook and Linux talk to the hub straight away">
    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[hsl(var(--success))] text-white"><Check className="h-8 w-8" strokeWidth={3} /></span>
    <div className="flex flex-wrap justify-center gap-1.5">{["Mac", "Chromebook", "Linux"].map((t) => <span key={t} className="rounded-full border bg-card px-2.5 py-1 text-xs font-semibold">{t}</span>)}</div>
  </Frame>
);
